import React from "react";
import CodeBlock from "@/components/CodeBlock";

const ReadyDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet CLI — ready Command
      </h2>

      <p className="mb-6">
        The <strong>ready</strong> command executes the full AxisNet Ready
        Pipeline and converts a freshly initialized AxisNet project into a
        complete, production-ready API foundation. All dependencies, references,
        base controllers, configuration files and resources are generated
        automatically.
      </p>

      {/* USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-10">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          Basic Usage
        </h3>

        <CodeBlock language="bash" code={`axisnet ready`} />

        <p className="mt-4">
          ⚠️ Before using <code>ready</code>, the <strong>init</strong> command
          must be completed. The init process defines:
        </p>

        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Project path</li>
          <li>Domain layer path</li>
          <li>Application layer path</li>
          <li>Infrastructure layer path</li>
          <li>Test layer path</li>
        </ul>

        <p className="mt-4">
          When init is done, <strong>ready</strong> automatically builds the
          entire AxisNet architecture.
        </p>
      </div>

      {/* PIPELINE OVERVIEW */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-10">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Ready Pipeline — Full Process
        </h3>

        <ol className="list-decimal pl-6 space-y-2">
          <li>Adds project references from ReferenceManifest.json</li>
          <li>Installs packages from PackageManifest.json</li>
          <li>Generates Program.cs</li>
          <li>Generates appsettings.json</li>
          <li>Creates localization resources (tr/en)</li>
          <li>Creates email templates</li>
          <li>Generates base system controllers</li>
          <li>
            Generates DependencyInjection.cs (Application & Infrastructure)
          </li>
          <li>Generates test helpers</li>
        </ol>
      </div>

      {/* STEP DETAILS */}
      <div className="space-y-10">
        {/* REFERENCES */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            1) Adding Project References
          </h4>
          <p className="mb-4">
            References are read from <strong>ReferenceManifest.json</strong> and
            automatically added to the related csproj files.
          </p>

          <CodeBlock
            language="json"
            code={`{
  "Base": [
    "../{{ProjectName}}.Application/{{ProjectName}}.Application.csproj",
    "../{{ProjectName}}.Infrastructure/{{ProjectName}}.Infrastructure.csproj",
    "../{{ProjectName}}.Tests/{{ProjectName}}.Tests.csproj"
  ],
  "Application": [
    "../{{ProjectName}}.Domain/{{ProjectName}}.Domain.csproj"
  ],
  "Infrastructure": [
    "../{{ProjectName}}.Application/{{ProjectName}}.Application.csproj",
    "../{{ProjectName}}.Domain/{{ProjectName}}.Domain.csproj"
  ],
  "Tests": [
    "../{{ProjectName}}.Application/{{ProjectName}}.Application.csproj",
    "../{{ProjectName}}.Domain/{{ProjectName}}.Domain.csproj",
    "../{{ProjectName}}.Infrastructure/{{ProjectName}}.Infrastructure.csproj"
  ]
}`}
          />
        </div>

        {/* PACKAGES */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            2) Installing Required Packages
          </h4>

          <p className="mb-4">
            Package installation list is read from{" "}
            <strong>PackageManifest.json</strong>.
          </p>

          <CodeBlock
            language="json"
            code={`{
  "Base": [
    { "package": "AxisNet.Framework", "version": "1.1.0" },
    { "package": "AspNetCore.HealthChecks.UI", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.UI.Client", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.SqlServer", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.Redis", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.UI.SqlServer.Storage", "version": "9.0.0" },
    { "package": "BCrypt.Net-Next", "version": "4.0.3" },
    { "package": "MediatR", "version": "13.0.0" },
    { "package": "Microsoft.AspNetCore.Authentication.JwtBearer", "version": "8.0.21" },
    { "package": "Microsoft.AspNetCore.Http.Abstractions", "version": "2.3.0" },
    { "package": "Microsoft.AspNetCore.Mvc.Versioning", "version": "5.1.0" },
    { "package": "Microsoft.AspNetCore.Mvc.Versioning.ApiExplorer", "version": "5.1.0" },
    { "package": "Microsoft.AspNetCore.OpenApi", "version": "8.0.11" },
    { "package": "UAParser", "version": "3.1.47" },
    { "package": "Microsoft.Extensions.Caching.StackExchangeRedis", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.Hosting.Abstractions", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.Http", "version": "9.0.10" },
    { "package": "Microsoft.IdentityModel.JsonWebTokens", "version": "8.14.0" },
    { "package": "Microsoft.IdentityModel.Tokens", "version": "8.14.0" },
    { "package": "StackExchange.Redis", "version": "2.9.32" },
    { "package": "Swashbuckle.AspNetCore", "version": "6.6.2" },
    { "package": "System.IdentityModel.Tokens.Jwt", "version": "8.14.0" },
    {
      "package": "Microsoft.EntityFrameworkCore.Design",
      "version": "9.0.10",
      "privateAssets": "all",
      "includeAssets": "runtime; build; native; contentfiles; analyzers; buildtransitive"
    }
  ],
  "Application": [
    { "package": "AxisNet.Framework", "version": "1.1.0" },
    { "package": "FluentValidation.DependencyInjectionExtensions", "version": "12.1.0" },
    { "package": "HtmlSanitizer", "version": "9.0.886" },
    { "package": "MediatR", "version": "13.0.0" },
    { "package": "AspNetCore.HealthChecks.Redis", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.SqlServer", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.UI", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.UI.Client", "version": "9.0.0" },
    { "package": "AspNetCore.HealthChecks.UI.SqlServer.Storage", "version": "9.0.0" },
    { "package": "AutoMapper", "version": "12.0.0" },
    { "package": "AutoMapper.Extensions.Microsoft.DependencyInjection", "version": "12.0.0" },
    { "package": "FluentValidation", "version": "12.1.0" },
    { "package": "Microsoft.Extensions.Configuration", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.DependencyInjection", "version": "9.0.10" },
    { "package": "Microsoft.EntityFrameworkCore", "version": "9.0.10" },
    { "package": "Microsoft.EntityFrameworkCore.SqlServer", "version": "9.0.10" },
    { "package": "Microsoft.EntityFrameworkCore.Tools", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.DependencyInjection.Abstractions", "version": "9.0.10" }
  ],
  "Domain": [
    { "package": "AxisNet.Framework", "version": "1.1.0" },
    { "package": "Microsoft.Extensions.Configuration", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.DependencyInjection", "version": "9.0.10" }
  ],
  "Infrastructure": [
    { "package": "AxisNet.Framework", "version": "1.1.0" },
    { "package": "Microsoft.EntityFrameworkCore", "version": "9.0.10" },
    { "package": "Microsoft.EntityFrameworkCore.SqlServer", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.Configuration", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.DependencyInjection", "version": "9.0.10" },
    { "package": "Microsoft.Extensions.DependencyInjection.Abstractions", "version": "9.0.10" }
  ],
  "Tests": [
    { "package": "AxisNet.Framework", "version": "1.1.0" },
    { "package": "FluentAssertions", "version": "8.8.0" },
    { "package": "Microsoft.NET.Test.Sdk", "version": "18.0.1" },
    { "package": "AutoMapper", "version": "12.0.0" },
    { "package": "AutoMapper.Extensions.Microsoft.DependencyInjection", "version": "12.0.0" },
    { "package": "AxisNet.Framework", "version": "1.1.0" },
    { "package": "coverlet.collector", "version": "6.0.0" },
    { "package": "Microsoft.NET.Test.Sdk", "version": "17.8.0" },
    { "package": "xunit", "version": "2.5.3" },
    { "package": "xunit.runner.visualstudio", "version": "2.5.3" }
  ]
}`}
          />
        </div>

        {/* PROGRAM.CS */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            3) Generating Program.cs
          </h4>

          <p className="mb-4">
            The ready pipeline generates a fully configured{" "}
            <strong>Program.cs</strong>
            file that bootstraps the entire API with Versioning, Authentication,
            Global Exception Handling, Secure Headers, CORS, Swagger, and
            AxisNet extensions. This Program.cs is production-grade and ready to
            run instantly.
          </p>

          <CodeBlock
            language="csharp"
            code={`using AxisNet.Application.Extensions;
using Microsoft.AspNetCore.Mvc.ApiExplorer;
using Microsoft.AspNetCore.Mvc.Authorization;
using {{ProjectName}}.Application;
using {{ProjectName}}.Infrastructure;

var builder = WebApplication.CreateBuilder(args);

// Add services to the CORS
builder.Services.AddAxisNetCorsPolicy("{{ProjectName}}", "http://localhost:3000");

// Add services to the Controller
builder.Services.AddControllers(options => options.Filters.Add(new AuthorizeFilter()));

// Add services to the Infrastructure
builder.Services.AddInfrastructure(builder.Configuration);

// Add services to the Application
builder.Services.AddApplication(builder.Configuration);

// Add services to the Authorization.
builder.Services.AddAuthorization();

// Add Services to the Versioning
builder.Services.AddAxisNetVersioning(1, 0);

// Add services to the Health Checks
builder.Services.AddAxisHealthChecks(
    builder.Configuration.GetConnectionString("SqlServer"),
    builder.Configuration.GetConnectionString("RedisServer")
);

// Learn more about configuring Swagger/OpenAPI at https://aka.ms/aspnetcore/swashbuckle
builder.Services.AddEndpointsApiExplorer();
builder.Services.AddAxisNetSwagger("{{ProjectName}}");

// App
var app = builder.Build();

// Configure the HTTP request pipeline.
if (app.Environment.IsDevelopment())
{
    app.UseSwagger();
    app.UseSwaggerUI(options =>
    {
        var provider = app.Services.GetRequiredService<IApiVersionDescriptionProvider>();
        foreach (var description in provider.ApiVersionDescriptions)
        {
            options.SwaggerEndpoint($"/swagger/{description.GroupName}/swagger.json", 
                description.GroupName.ToUpperInvariant());
        }
    });
}
else
{
    app.UseHsts();
}

app.UseRouting();

app.UseAxisNetGlobalExceptionMiddleware(builder.Configuration);

app.UseAxisNetSecureHeaderMiddleware();

app.UseForwardedHeaders();

app.UseHttpsRedirection();

app.UseCors("{{ProjectName}}");

app.UseAuthentication();

app.UseAuthorization();

app.UseAxisNetHealth();

app.MapControllers();

app.Run();`}
          />

          <p className="mt-4">
            This file represents the complete AxisNet startup pipeline including
            CORS, Swagger, API Versioning, Exception Handling, Secure Headers,
            Authentication, Authorization, Health Checks and routing.
          </p>
        </div>

        {/* APPSETTINGS */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            4) Generating appsettings.json
          </h4>

          <p className="mb-4">
            The ready pipeline generates a fully prepared{" "}
            <strong>appsettings.json</strong>
            file containing all required configuration sections for a
            production-ready API.
          </p>

          <CodeBlock
            language="json"
            code={`{
  "Logging": {
    "LogLevel": {
      "Default": "Information",
      "Microsoft.AspNetCore": "Warning"
    }
  },
  "App": {
      "FrontendUrl": ""
  },
  "ConnectionStrings": {
    "RedisServer": "",
    "SqlServer": ""
  },
  "RecaptchaOptions": {
    "SecretKey": "",
    "VerifyUrl": ""
  },
  "JwtOptions": {
    "Issuer": "",
    "Audience": "",
    "SecretKey": "",
    "ExpiresInMinutes": 0
  },
  "MailOptions": {
    "DisplayName": "",
    "Port": 0,
    "Encryption": "",
    "Password": "",
    "Host": "",
    "Username": "",
    "From": ""
  },
  "Developer": {
    "Mail": ""
  },
  "AllowedHosts": "*"
}`}
          />

          <p className="mt-4">
            This configuration file ensures JWT authentication, mailing, Redis,
            database, Google reCAPTCHA, and logging systems are ready for use
            immediately.
          </p>
        </div>

        {/* RESOURCES */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            5) Generating Localization Resources (tr/en)
          </h4>

          <p className="mb-4">
            The ready pipeline automatically generates multilingual resource
            files (<strong>tr.json</strong> and <strong>en.json</strong>) used
            across the entire AxisNet architecture for:
          </p>

          <ul className="list-disc pl-6 space-y-1">
            <li>Exception messages</li>
            <li>Service messages (Auth, Login, Logout, Two-Factor)</li>
            <li>Authorization & Behavior messages</li>
            <li>
              All validation rules (User, Auth, Roles, Permissions, Filters,
              GUID, Pagination…)
            </li>
          </ul>

          <p className="mt-4 mb-4">
            These files enable full internationalization support and power the
            <strong>AxisNet.LocalizationService</strong> used in Application &
            Infrastructure layers.
          </p>

          <h4 className="text-md font-semibold text-emerald-400 mb-2">
            Generated Files
          </h4>

          <CodeBlock
            language="bash"
            code={`Resources/
├── en.json       # English messages
└── tr.json       # Turkish messages`}
          />

          <h4 className="text-md font-semibold text-white mt-4 mb-2">
            Sample (English)
          </h4>

          <CodeBlock
            language="json"
            code={`{
  "Exceptions": {
    "Roles": {
      "NotFound": "The requested resource (role) could not be found."
    },
    "Permissions": {
      "NotFound": "The requested resource (permission) could not be found."
    },
    "Sessions": {
      "NotFound": "The requested session information could not be retrieved.",
      "InvalidRefreshToken": "The provided refresh token does not match the active session.",
      "AccessTokenNotExpired": "The access token is still valid and cannot be refreshed yet."
    },
    "...": "..."
  }
}`}
          />

          <h4 className="text-md font-semibold text-white mt-4 mb-2">
            Sample (Turkish)
          </h4>

          <CodeBlock
            language="json"
            code={`{
  "Exceptions": {
    "Roles": {
      "NotFound": "İstenen kaynak (rol) bulunamadı."
    },
    "Permissions": {
      "NotFound": "İstenen kaynak (izin) bulunamadı."
    },
    "Sessions": {
      "NotFound": "Talep edilen oturum kayıt bilgisine ulaşılamamıştır.",
      "InvalidRefreshToken": "Geçersiz yenileme belirteci. Lütfen oturumu yeniden başlatın.",
      "AccessTokenNotExpired": "Access token henüz geçerli. Yenileme işlemi yapılamaz."
    },
    "...": "..."
  }
}`}
          />

          <p className="mt-4">
            Both files are fully structured and production-ready. You can add
            new keys, extend modules, or integrate them with your custom
            features.
          </p>
        </div>

        {/* EMAIL */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            6) Generating Email Templates
          </h4>

          <ul className="list-disc pl-6 space-y-1">
            <li>ChangeEmail.html</li>
            <li>TwoFactorEmail.html</li>
            <li>ErrorMessageEmail.html</li>
            <li>ForgotPasswordEmail.html</li>
            <li>VerifyEmail.html</li>
          </ul>
        </div>

        {/* CONTROLLERS */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            7) Generating Base Controllers
          </h4>

          <ul className="list-disc pl-6 space-y-1">
            <li>AuthController</li>
            <li>UsersController</li>
            <li>RolesController</li>
            <li>PermissionsController</li>
            <li>SessionsController</li>
            <li>LogsController</li>
          </ul>
        </div>

        {/* DI FILES */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            8) Generating DependencyInjection Files
          </h4>

          <CodeBlock
            language="csharp"
            code={`services.AddScoped<IUserRepository, UserRepository>();`}
          />
        </div>

        {/* TEST HELPERS */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-2">
            9) Creating Test Helpers
          </h4>

          <p>Automatically generates:</p>

          <ul className="list-disc pl-6">
            <li>FakeLocalizationService</li>
            <li>TestValueGenerator</li>
          </ul>
        </div>
      </div>

      {/* FINAL RESULT */}
      <div className="bg-slate-900/60 border border-slate-700 p-6 rounded-lg mt-14">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Final Result
        </h3>

        <p className="mb-4">
          After running <code>axisnet ready</code>, your project becomes
          completely operational and ready for feature generation.
        </p>

        <p className="mt-4 text-emerald-400 font-semibold">Next step:</p>

        <CodeBlock
          language="bash"
          code={`axisnet add feature --name Product`}
        />
      </div>
    </section>
  );
};

export default ReadyDoc;
